/**
 * ExpertGenerationTimeline - Expert-only generation internals viewer
 *
 * Panel showing the full generation history as one tab per attempt (retry).
 * Each tab carries a run-status dot and the attempt's cost; the LAST (most
 * relevant) attempt is selected by default and only the active tab's steps
 * mount. Steps render as a reviewer-friendly table (GenerationStepsTable)
 * with plain-language action labels and per-step thumbs up/down + note
 * feedback.
 *
 * The header's "cost receipt" sums what failed runs burned — the panel's one
 * loud element; everything else stays quiet.
 *
 * Gated by canViewGenerationInternals (expert or an elevated platform role);
 * the backend endpoint 403s for anyone else. The blog detail page renders
 * this inside its expert-only "Generation internals" tab, which only mounts
 * the component while active — fetching starts on mount, so laziness is
 * enforced by the tab, not by a collapse toggle.
 */

import React, { useMemo, useState } from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import GenerationStepsTable, {
  type StepsTableFeedbackConfig,
} from './GenerationStepsTable';
import {
  useGenerationTimeline,
  useGenerationFeedback,
  useSubmitStepFeedback,
} from '@/hooks/useGenerationTimeline';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type {
  GenerationAttempt,
  GenerationAttemptOutcome,
  GenerationRunSummary,
  StepFeedback,
} from './types';

interface ExpertGenerationTimelineProps {
  contentId: string;
}

function formatCost(cost: number | null | undefined): string {
  if (cost == null) return '—';
  return `$${cost.toFixed(2)}`;
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function isFailedRun(run: GenerationRunSummary | null): boolean {
  const status = run?.status?.toLowerCase();
  return status === 'failed' || status === 'error';
}

function isCompletedRun(run: GenerationRunSummary | null): boolean {
  const status = run?.status?.toLowerCase();
  return status === 'completed' || status === 'success';
}

/** Status dot for an attempt tab: destructive=failed, teal=completed, coral(pulse)=running */
function attemptDotClass(attempt: GenerationAttempt): string {
  if (isFailedRun(attempt.run)) return 'bg-destructive';
  if (isCompletedRun(attempt.run)) return 'bg-teal';
  if (attempt.outcome === 'in_progress' || attempt.run?.status?.toLowerCase() === 'running') {
    return 'bg-coral animate-pulse';
  }
  return 'bg-slate-300';
}

/** Tab tooltip — always phrased as a RUN outcome, never the article status. */
function attemptTooltip(attempt: GenerationAttempt): string {
  const outcome = attempt.run ? `run ${attempt.run.status?.toLowerCase() || 'unknown'}` : 'no run recorded';
  return `${outcome} · ${formatDuration(attempt.duration_ms)}`;
}

const outcomeBadge: Record<GenerationAttemptOutcome, { label: string; className: string }> = {
  superseded: { label: 'superseded', className: 'bg-muted text-muted-foreground' },
  final: { label: 'final', className: 'bg-teal-light text-teal-dark' },
  in_progress: { label: 'in progress', className: 'bg-coral-light text-coral-dark' },
};

/**
 * Badge for the writer RUN outcome. Always phrased as a run outcome
 * ("run failed") — never as the article status, since a failed run can
 * belong to a superseded attempt of a successfully completed article.
 */
const RunBadge: React.FC<{ run: GenerationRunSummary }> = ({ run }) => {
  const status = run.status?.toLowerCase() || 'unknown';
  const className = isFailedRun(run)
    ? 'bg-destructive/10 text-destructive'
    : isCompletedRun(run)
      ? 'bg-teal-light text-teal-dark'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${className}`}>
      run {status}
    </span>
  );
};

const ExpertGenerationTimeline: React.FC<ExpertGenerationTimelineProps> = ({ contentId }) => {
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  const userProfile = useWorkspaceStore((state) => state.userProfile);
  const currentAuthor = userProfile?.email;

  // Fetch starts on mount — the page's expert-only tab only mounts this
  // component while active, which keeps the endpoints lazy.
  const { data, isLoading, isError } = useGenerationTimeline(contentId);
  const { data: feedbackData } = useGenerationFeedback(contentId);
  const submitFeedback = useSubmitStepFeedback(contentId, currentAuthor);

  const feedbackByIndex = useMemo(() => {
    const byIndex: Record<number, StepFeedback[]> = {};
    for (const item of feedbackData?.items ?? []) {
      (byIndex[item.step_index] ??= []).push(item);
    }
    return byIndex;
  }, [feedbackData]);

  // Lookup so submits can attach the step's tool/ts for backend provenance
  const entryByIndex = useMemo(() => {
    const map = new Map<number, { tool?: string; ts?: string }>();
    for (const attempt of data?.attempts ?? []) {
      for (const entry of attempt.entries) {
        map.set(entry.index, { tool: entry.tool, ts: entry.ts });
      }
    }
    return map;
  }, [data]);

  const feedbackConfig: StepsTableFeedbackConfig = useMemo(
    () => ({
      byIndex: feedbackByIndex,
      currentAuthor,
      onSubmit: (stepIndex, verdict, note, expectedOutput) => {
        const entry = entryByIndex.get(stepIndex);
        submitFeedback.mutate({
          step_index: stepIndex,
          step_tool: entry?.tool,
          step_ts: entry?.ts,
          verdict,
          note,
          expected_output: expectedOutput,
        });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feedbackByIndex, currentAuthor, entryByIndex],
  );

  const attempts = data?.attempts ?? [];

  // Cost receipt: what the failed runs burned (attempts only, per spec)
  const failedAttempts = attempts.filter((attempt) => isFailedRun(attempt.run));
  const wastedCost = failedAttempts.reduce(
    (sum, attempt) => sum + (attempt.run?.total_cost_usd ?? 0),
    0,
  );

  // Default-select the LAST attempt (the final / most relevant one)
  const lastAttempt = attempts[attempts.length - 1];
  const activeTab =
    selectedTab ?? (lastAttempt ? String(lastAttempt.attempt_number) : undefined);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Panel header — summary only; the page tab controls visibility */}
      <div className="w-full flex items-center justify-between gap-3 flex-wrap px-3 py-2 bg-muted">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Generation internals</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-coral-light text-coral-dark font-medium">
            expert
          </span>
        </div>
        {data && (
          <span className="text-xs text-muted-foreground">
            {attempts.length} attempt{attempts.length === 1 ? '' : 's'} · {formatCost(data.total_cost_usd)}
            {failedAttempts.length > 0 && (
              <>
                {' · '}
                {failedAttempts.length} failed run{failedAttempts.length === 1 ? '' : 's'} burned{' '}
                <span className="text-destructive font-medium">{formatCost(wastedCost)}</span>
              </>
            )}
          </span>
        )}
      </div>

      <div>
        {isLoading && (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading generation timeline…
          </div>
        )}
        {isError && (
          <div className="px-3 py-4 text-xs text-destructive">
            Failed to load generation timeline.
          </div>
        )}
        {data && attempts.length === 0 && (
          <div className="px-3 py-4 text-xs text-muted-foreground">No generation attempts recorded.</div>
        )}

        {/* One tab per attempt; only the active tab's steps mount */}
        {attempts.length > 0 && (
          <Tabs value={activeTab} onValueChange={setSelectedTab}>
            <TabsList className="w-full h-auto justify-start gap-0 rounded-none border-b border-border bg-transparent p-0 overflow-x-auto">
              {attempts.map((attempt) => (
                <TabsTrigger
                  key={attempt.attempt_number}
                  value={String(attempt.attempt_number)}
                  title={attemptTooltip(attempt)}
                  className="gap-1.5 rounded-none border-b-2 border-transparent px-3 py-2 text-xs text-muted-foreground data-[state=active]:border-coral data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${attemptDotClass(attempt)}`}
                  />
                  Attempt {attempt.attempt_number}
                  <span className="tabular-nums text-muted-foreground">
                    {formatCost(attempt.run?.total_cost_usd)}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
            {attempts.map((attempt) => {
              const badge = outcomeBadge[attempt.outcome] ?? outcomeBadge.superseded;
              return (
                <TabsContent
                  key={attempt.attempt_number}
                  value={String(attempt.attempt_number)}
                  className="mt-0"
                >
                  {/* Quiet meta strip for the selected attempt */}
                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border text-[11px] text-muted-foreground">
                    <span>{formatDuration(attempt.duration_ms)}</span>
                    <span>·</span>
                    <span>{attempt.entry_count} steps</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                    {attempt.run && <RunBadge run={attempt.run} />}
                  </div>
                  <GenerationStepsTable entries={attempt.entries} feedback={feedbackConfig} />
                </TabsContent>
              );
            })}
          </Tabs>
        )}

        {/* Runs the backend couldn't match to a logged attempt */}
        {data && data.unmatched_runs.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-1">
              Runs not matched to a logged attempt
            </div>
            {data.unmatched_runs.map((run) => (
              <div key={run.run_id} className="flex items-center gap-2 py-0.5 text-xs text-muted-foreground">
                <span className="font-mono truncate">{run.run_id}</span>
                <RunBadge run={run} />
                <span>{formatCost(run.total_cost_usd)}</span>
                {run.duration_seconds != null && (
                  <span>{formatDuration(run.duration_seconds * 1000)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpertGenerationTimeline;
